import { Check } from 'lucide-react';

type Variant = 'header' | 'sidebar' | 'mobile-hero';

interface AuthBrandingProps {
    variant?: Variant;
    title?: string;
    subtitle?: string;
    features?: string[];
}

function Logo() {
    return (
        <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Restroos" className="h-10 w-10 rounded-xl" />
            <span className="text-xl font-semibold tracking-tight">
                <span>Res</span><span className="text-accent">troos</span>
            </span>
        </div>
    );
}

export function AuthBranding({
    variant = 'header',
    title,
    subtitle,
    features,
}: AuthBrandingProps) {
    if (variant === 'header') {
        return <Logo />;
    }

    if (variant === 'mobile-hero') {
        return (
            <div
                className="relative overflow-hidden lg:hidden"
                style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary" />
                <div className="relative z-10 px-6 pt-7 pb-10 text-primary-foreground">
                    <Logo />
                    {title && (
                        <h1 className="mt-5 text-2xl font-bold leading-snug">{title}</h1>
                    )}
                    {subtitle && (
                        <p className="mt-2 text-sm text-primary-foreground/75 leading-relaxed max-w-xs">
                            {subtitle}
                        </p>
                    )}
                    {features && features.length > 0 && (
                        <div className="mt-4 flex flex-col gap-2.5">
                            {features.map((feature, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/25 shrink-0">
                                        <Check className="h-3 w-3 text-accent" />
                                    </div>
                                    <span className="text-sm text-primary-foreground/85">{feature}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-accent/15 blur-3xl" />
                <div className="pointer-events-none absolute -right-8 top-10 h-28 w-28 rounded-full bg-accent/10 blur-2xl" />
            </div>
        );
    }

    return (
        <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 gradient-slate">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary" />
            <div className="relative z-10 flex flex-col justify-between p-12 text-primary-foreground">
                <Logo />
                {(title || subtitle || features) && (
                    <div className="space-y-6">
                        {title && (
                            <h1 className="text-4xl font-bold leading-tight">{title}</h1>
                        )}
                        {subtitle && (
                            <p className="max-w-md text-lg text-primary-foreground/80">
                                {subtitle}
                            </p>
                        )}
                        {features && features.length > 0 && (
                            <div className="space-y-4">
                                {features.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/20">
                                            <Check className="h-4 w-4 text-accent" />
                                        </div>
                                        <span className="text-primary-foreground/90">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <p className="text-sm text-primary-foreground/60">
                    © 2026 Restroos. All rights reserved.
                </p>
            </div>
            <div className="absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
            <div className="absolute -right-10 top-20 h-40 w-40 rounded-full bg-accent/10 blur-2xl" />
        </div>
    );
}
